import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles, Ticket, CreditCard, ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import JobCard, { Listing } from '@/components/JobCard';
import AddonsPanel from '@/components/AddonsPanel';
import LeadBalanceCard from '@/components/LeadBalanceCard';
import AccountNotices from '@/components/AccountNotices';

import { fetchListings, fetchPurchasedIds } from '@/lib/marketplace';
import { getLeadBalance, LeadBalance, EMPTY_BALANCE, formatPeriodEnd } from '@/lib/leadBalance';
import { money, tradeLabel, localizedSlug } from '@/data/constants';


const STAGES = ['purchased', 'contacted', 'estimate', 'won', 'lost'];

interface PurchaseRow {
  id: string;
  listing_id: string;
  amount: number;
  stage: string;
  created_at: string;
}

const SubDashboard: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const [jobs, setJobs] = useState<Listing[]>([]);
  const [purchasedIds, setPurchasedIds] = useState<string[]>([]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [leadListings, setLeadListings] = useState<Record<string, Listing>>({});
  const [sub, setSub] = useState<{ plan_code: string; amount: number; status: string } | null>(null);
  const [balance, setBalance] = useState<LeadBalance>(EMPTY_BALANCE);

  const loadPurchases = async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('mkt_lead_purchases')
      .select('*')
      .eq('buyer_id', profile.id)
      .order('created_at', { ascending: false });
    const rows = (data || []) as PurchaseRow[];
    setPurchases(rows);
    setPurchasedIds(rows.map((r) => r.listing_id));
    if (rows.length) {
      const { data: ls } = await supabase
        .from('mkt_listings')
        .select('*')
        .in('id', rows.map((r) => r.listing_id));
      const map: Record<string, Listing> = {};
      (ls || []).forEach((l: Listing) => (map[l.id] = l));
      setLeadListings(map);
    }
  };

  useEffect(() => {
    fetchListings({ limit: 12 }).then(setJobs);
    fetchPurchasedIds(profile?.id).then(setPurchasedIds);
    loadPurchases();
    if (profile) {
      getLeadBalance(profile.id).then(setBalance);
      supabase
        .from('mkt_subscriptions')
        .select('*')
        .eq('user_id', profile.id)
        .in('status', ['active', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .then(({ data }) => setSub(data && data[0] ? data[0] : null));
    }
  }, [profile?.id]);

  const myTrades = profile?.trades || [];
  const myAreas = (profile?.service_areas || []).map((s) => s.toLowerCase());
  const recommended = jobs.filter(
    (j) => myTrades.includes(j.trade) || myAreas.includes((j.city || '').toLowerCase())
  );

  const setStage = async (id: string, stage: string) => {
    await supabase.from('mkt_lead_purchases').update({ stage }).eq('id', id);
    setPurchases((p) => p.map((r) => (r.id === id ? { ...r, stage } : r)));
  };

  return (
    <div>
      {/* SECTION 0 — MONTHLY LEAD BALANCE / COUNTDOWN */}
      <section className="mb-12">
        <LeadBalanceCard balance={balance} amount={sub?.amount} />
        {/* Payment, membership, credit and lead-unlock notices mirrored from
            the transactional emails, so delivery is never the only path. */}
        <AccountNotices />
      </section>


      {/* SECTION 1 — AVAILABLE JOBS */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <Search className="h-5 w-5 text-orange-500" /> {t('dash.availableJobs')}
          </h2>
          <Link to={path('/jobs')} className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-600">
            {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {jobs.slice(0, 6).map((j) => (
            <JobCard
              key={j.id}
              job={j}
              unlocked={purchasedIds.includes(j.id)}
              onUnlock={() => window.location.assign(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`))}

            />
          ))}
        </div>
      </section>

      {/* SECTION 2 — RECOMMENDED */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Sparkles className="h-5 w-5 text-orange-500" /> {t('dash.recommended')}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t('dash.recommendedSub')}</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {(recommended.length ? recommended : jobs.slice(6, 9)).slice(0, 3).map((j) => (
            <JobCard
              key={j.id}
              job={j}
              unlocked={purchasedIds.includes(j.id)}
              onUnlock={() => window.location.assign(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`))}

            />
          ))}
        </div>
      </section>

      {/* SECTION 3 — MY PURCHASED LEADS */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Ticket className="h-5 w-5 text-orange-500" /> {t('dash.myLeads')}
        </h2>
        {purchases.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            {t('dash.noLeads')}
          </p>
        ) : (
          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {purchases.map((p) => {
              const l = leadListings[p.listing_id];
              return (
                <div
                  key={p.id}
                  className="flex flex-col gap-3 border-b border-slate-100 p-4 last:border-0 sm:flex-row sm:items-center"
                >
                  <div className="flex-1">
                    <Link
                      to={l ? path(`/jobs/${localizedSlug(l.slug, l.trade, lang)}`) : '#'}
                      className="font-bold text-slate-900 hover:text-orange-600"
                    >

                      {l?.title || t('common.loading')}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {l ? `${tradeLabel(l.trade, lang)} · ${l.city}, ${l.state}` : ''} ·{' '}
                      {p.amount === 0 ? t('pay.useIncluded') : money(p.amount)}
                    </p>
                  </div>
                  <select
                    value={p.stage}
                    onChange={(e) => setStage(p.id, e.target.value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800"
                  >
                    {STAGES.map((s) => (
                      <option key={s} value={s}>
                        {t(`dash.stage.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* SECTION 4 — MY SUBSCRIPTION */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <CreditCard className="h-5 w-5 text-orange-500" /> {t('dash.mySubscription')}
        </h2>
        <div className="mt-5 flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center">
          <div>
            {sub ? (
              <>
                <p className="text-lg font-extrabold uppercase text-slate-900">{sub.plan_code}</p>
                <p className="text-sm text-slate-500">
                  {money(sub.amount)}
                  {t('pricing.perMonth')}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-600">{t('dash.noSubscription')}</p>
            )}
            <p className="mt-2 text-sm font-semibold text-emerald-700">
              {t('dash.includedLeads')}: {balance.remaining}
              {balance.included > 0 && <span className="text-slate-400"> / {balance.included}</span>}
            </p>
            {balance.periodEnd && (
              <p className="text-xs text-slate-400">
                {t('leads.renews')}: {formatPeriodEnd(balance.periodEnd, lang)}
              </p>
            )}
          </div>
          <Link to={path('/pricing')} className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800">
            {t('dash.viewPlans')}
          </Link>
        </div>
      </section>

      <AddonsPanel />
    </div>
  );
};

export default SubDashboard;
